import PropTypes from "prop-types";
import React from "react";
import { Grid, TextField, makeStyles } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { DateTime } from "luxon";
import spTheme from "../../spTheme";

/*
 Note:
 This component uses the TextField component which has a few restrictions.
 One of the ways we accomplish the display we desire is to hide the actual
 input element and instead display what we want in the label.
 This is not a pattern that is encouraged.
 */

const useStyles = makeStyles(() => ({
  input: {
    padding: 0,
    margin: 0,
    opacity: 0,
    height: 32,
  },
  root: {
    "& .MuiInputLabel-formControl": { transform: "none" },
    "& .MuiInput-underline::after": { display: "none" },
    "& .MuiInput-formControl": { position: "relative", transform: "none" },
    "& label + .MuiInput-formControl": { margin: 0, padding: 0 },
    borderRadius: 100,
    height: 32,
    marginRight: 10,
    "& ::-webkit-calendar-picker-indicator": {
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      margin: 0,
      padding: 0,
      opacity: 0,
    },
    backgroundColor: spTheme.palette.background.selected,
  },
  label: {
    zIndex: 1,
    pointerEvents: "none",
    width: "100%",
    textAlign: "center",
    lineHeight: "2.3rem",
    color: spTheme.palette.text.primary,
    fontSize: 13,
    fontStyle: "normal",
    fontWeight: 400,
    letterSpacing: "0.16px",
  },
}));

export const DateRangeChips = ({
  onChangeFloor,
  onChangeCeil,
  floor,
  ceil,
  minDate,
  maxDate,
  InputGridProps,
  InputLabelProps,
  InputProps,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const onChange = ({ target: { value } }, handler) => handler(value.trim());

  const fromIsToday = floor === DateTime.now().toFormat("yyyy-MM-dd");
  const toIsToday = ceil === DateTime.now().toFormat("yyyy-MM-dd");

  return (
    <Grid container item sx={12} md={6} {...InputGridProps}>
      <TextField
        className={classes.root}
        style={{ width: fromIsToday ? 93 : 134 }}
        value={floor}
        label={fromIsToday ? "From: Today" : `From: ${DateTime.fromISO(floor).toFormat("DD")}`}
        type="date"
        onChange={(e) => onChange(e, onChangeFloor)}
        InputLabelProps={{ className: classes.label, ...InputLabelProps }}
        InputProps={{
          inputProps: {
            className: classes.input,
            min: minDate,
            max: ceil,
          },
          ...InputProps,
        }}
      />
      <TextField
        className={classes.root}
        style={{ width: toIsToday ? 77 : 118 }}
        value={ceil}
        label={toIsToday ? "To: Today" : `To: ${DateTime.fromISO(ceil).toFormat("DD")}`}
        type="date"
        onChange={(e) => onChange(e, onChangeCeil)}
        InputLabelProps={{ className: classes.label, ...InputLabelProps }}
        InputProps={{
          inputProps: {
            className: classes.input,
            min: floor,
            max: maxDate,
          },
          ...InputProps,
        }}
      />
    </Grid>
  );
};

DateRangeChips.propTypes = {
  onChangeFloor: PropTypes.func.isRequired,
  onChangeCeil: PropTypes.func.isRequired,
  floor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ceil: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  InputGridProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  InputProps: PropTypes.object,
};

DateRangeChips.defaultProps = {
  floor: undefined,
  ceil: undefined,
  minDate: "",
  maxDate: "",
  InputGridProps: {},
  InputLabelProps: {},
  InputProps: {},
};

export default DateRangeChips;
